import {
  SoftwarePackage,
  SoftwareSelection,
  SoftwareStats,
} from "../../types/software";
import { ComputerSelection } from "../../types/computer";
import { SOFTWARE_PACKAGES } from "../../constants/software";

export const calculateSoftwareBandwidth = (
  softwareSelection: SoftwareSelection,
  computerSelection: ComputerSelection,
): number => {
  console.log("calculateSoftwareBandwidth called with:", { softwareSelection, computerSelection });
  let totalBandwidth = 0;

  // Check if softwareSelection and running array exist
  if (!softwareSelection || !softwareSelection.running) {
    console.log("calculateSoftwareBandwidth: softwareSelection or running array is undefined/null");
    return totalBandwidth;
  }

  // Check if the ACTIVE computer (primary) has Jump Control Specialisation (/bis) option
  // Only the primary computer runs software, so only its /bis option matters
  const hasJumpControlSpecialisation = !!(
    computerSelection.primary?.options.includes("jumpControl") ||
    computerSelection.primary?.options.includes("both")
  );

  console.log("calculateSoftwareBandwidth: hasJumpControlSpecialisation =", hasJumpControlSpecialisation);

  // Calculate bandwidth for running software
  softwareSelection.running.forEach((running) => {
    if (running && running.running) {
      const package_ = SOFTWARE_PACKAGES.find(
        (p) => p.id === running.packageId,
      );
      if (package_) {
        let bandwidth = 0;
        
        if (package_.levels) {
          // Multi-level software
          const level = package_.levels.find((l) => l.level === running.level);
          if (level) {
            bandwidth = level.bandwidth;
          }
        } else {
          // Single-level software
          bandwidth = package_.bandwidth;
        }

        // Apply Jump Control Specialisation (/bis) reduction for Jump Control software
        if (package_.id === "jumpControl" && hasJumpControlSpecialisation) {
          const originalBandwidth = bandwidth;
          const reduction = 5; // Reduce bandwidth by 5 points per level
          bandwidth = Math.max(0, bandwidth - reduction); // Ensure bandwidth doesn't go below 0
          console.log(`calculateSoftwareBandwidth: Jump Control level ${running.level} bandwidth reduced from ${originalBandwidth} to ${bandwidth} due to /bis option`);
        }

        totalBandwidth += bandwidth;
      }
    }
  });

  console.log("calculateSoftwareBandwidth: totalBandwidth =", totalBandwidth);
  return totalBandwidth;
};

export const calculateSoftwareCost = (
  softwareSelection: SoftwareSelection,
  computerSelection: ComputerSelection,
): number => {
  let totalCost = 0;

  // Check if softwareSelection and installed array exist
  if (!softwareSelection || !softwareSelection.installed) {
    return totalCost;
  }

  // Calculate cost for installed software
  softwareSelection.installed.forEach((installed) => {
    if (installed && installed.installed) {
      const package_ = SOFTWARE_PACKAGES.find(
        (p) => p.id === installed.packageId,
      );
      if (package_) {
        if (package_.levels) {
          // Multi-level software
          const level = package_.levels.find(
            (l) => l.level === installed.level,
          );
          if (level) {
            totalCost += level.cost;
          }
        } else {
          // Single-level software
          totalCost += package_.cost;
        }
      }
    }
  });

  return totalCost;
};

/**
 * Calculate software cost from a string array (for current state structure)
 * @param softwareIds - Array of software package IDs
 * @param computerSelection - Computer selection
 * @returns Total software cost
 */
export const calculateSoftwareCostFromStrings = (
  softwareIds: string[],
  computerSelection: ComputerSelection,
): number => {
  let totalCost = 0;

  // Calculate cost for each software package
  softwareIds.forEach((softwareId) => {
    const package_ = SOFTWARE_PACKAGES.find((p) => p.id === softwareId);
    if (package_) {
      // For now, assume level 1 (base level) for all software
      // This can be enhanced later to support multiple levels
      totalCost += package_.cost;
    }
  });

  return totalCost;
};

export const getAvailableBandwidth = (
  computerSelection: ComputerSelection,
): number => {
  if (!computerSelection || !computerSelection.primary) return 0;

  return computerSelection.primary.processing;
};

export const validateSoftwareSelection = (
  softwareSelection: SoftwareSelection,
  computerSelection: ComputerSelection,
  shipTechLevel: number,
  tlOverrideActive: boolean = false,
): { isValid: boolean; errors: string[] } => {
  console.log("validateSoftwareSelection called with:", { softwareSelection, computerSelection, shipTechLevel, tlOverrideActive });
  const errors: string[] = [];
  
  // Check if softwareSelection exists
  if (!softwareSelection) {
    console.log("validateSoftwareSelection: softwareSelection is undefined/null");
    return {
      isValid: true,
      errors: [],
    };
  }

  const availableBandwidth = getAvailableBandwidth(computerSelection);
  const usedBandwidth = calculateSoftwareBandwidth(
    softwareSelection,
    computerSelection,
  );

  // Check bandwidth usage
  if (usedBandwidth > availableBandwidth) {
    errors.push(
      `Software bandwidth usage (${usedBandwidth}) exceeds available bandwidth (${availableBandwidth})`,
    );
  }

  // Check that running software is installed
  if (softwareSelection.running) {
    softwareSelection.running.forEach((running) => {
      if (running && running.running) {
        const isInstalled = (softwareSelection.installed || []).some(
          (installed) =>
            installed.packageId === running.packageId &&
            installed.level === running.level &&
            installed.installed,
        );
        if (!isInstalled) {
          const package_ = SOFTWARE_PACKAGES.find(
            (p) => p.id === running.packageId,
          );
          errors.push(
            `${package_?.name || running.packageId} must be installed before it can run`,
          );
        }
      }
    });
  }

  // Check TL requirements (use ship TL, not computer TL)
  if (!tlOverrideActive && softwareSelection.installed) {
    softwareSelection.installed.forEach((installed) => {
      if (installed && installed.installed) {
        const package_ = SOFTWARE_PACKAGES.find(
          (p) => p.id === installed.packageId,
        );
        if (package_) {
          let requiredTL = package_.tl;
          if (package_.levels) {
            const level = package_.levels.find(
              (l) => l.level === installed.level,
            );
            if (level) {
              requiredTL = level.tl;
            }
          }
          if (shipTechLevel < requiredTL) {
            errors.push(
              `${package_.name} requires TL ${requiredTL}, but ship is TL ${shipTechLevel}`,
            );
          }
        }
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const calculateSoftwareStats = (
  softwareSelection: SoftwareSelection,
  computerSelection: ComputerSelection,
): SoftwareStats => {
  const totalBandwidth = calculateSoftwareBandwidth(
    softwareSelection,
    computerSelection,
  );
  const totalCost = calculateSoftwareCost(softwareSelection, computerSelection);
  const availableBandwidth = getAvailableBandwidth(computerSelection);
  const installedPackages = (softwareSelection?.installed || []).filter(
    (s) => s && s.installed,
  ).length;
  const runningPackages = (softwareSelection?.running || []).filter(
    (s) => s && s.running,
  ).length;

  return {
    totalBandwidth,
    totalCost,
    availableBandwidth,
    installedPackages,
    runningPackages,
  };
};

export const getSoftwarePackage = (
  packageId: string,
): SoftwarePackage | undefined => {
  return SOFTWARE_PACKAGES.find((p) => p.id === packageId);
};

export const getSoftwarePackageLevel = (
  packageId: string,
  level: number,
):
  | { bandwidth: number; cost: number; tl: number; description?: string }
  | undefined => {
  const package_ = getSoftwarePackage(packageId);
  if (!package_ || !package_.levels) return undefined;

  return package_.levels.find((l) => l.level === level);
};

export const getSoftwareCategories = (): string[] => {
  const categories = new Set(SOFTWARE_PACKAGES.map((p) => p.category));
  return Array.from(categories).sort();
};

export const getSoftwareByCategory = (category: string): SoftwarePackage[] => {
  if (category === "" || category === "All") {
    return SOFTWARE_PACKAGES;
  }
  return SOFTWARE_PACKAGES.filter((p) => p.category === category);
};
